import React from 'react';
import { Box, Text, VStack, HStack, SimpleGrid, Stack } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import Header from './Header';
import { useSettings } from '../hooks/useApi';

// On the scheduling subdomain, footer links point back to the full main site
// rather than routing within the booking microsite. See Header.js for context.
const ON_SCHEDULING_SUBDOMAIN =
  typeof window !== 'undefined' && window.location.hostname === 'scheduling.cloverridgetech.com';
const MAIN_SITE = 'https://cloverridgetech.com';

const Link = ({ to, children, ...rest }) =>
  ON_SCHEDULING_SUBDOMAIN ? (
    <a href={`${MAIN_SITE}${to}`} {...rest}>{children}</a>
  ) : (
    <RouterLink to={to} {...rest}>{children}</RouterLink>
  );

const Layout = ({ children }) => {
  const { data: settings } = useSettings();
  
  return (
    <Box minH="100vh" bg="black" color="white">
      <Header />
      
      {/* Main Content */}
      <Box as="main">
        {children}
      </Box>
      
      {/* Footer - Mobile Responsive */}
      <Box py={{ base: 8, md: 16 }} px={{ base: 4, md: 8 }} borderTop="1px solid" borderColor="gray.800" w="100%">
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: 8, md: 16, lg: 32 }}
          align={{ base: "start", md: "start" }}
          justify={{ base: "start", md: "space-between" }}
          maxW="5xl"
          mx="auto"
        >
          {/* Contact Section */}
          <VStack align="start" spacing={6} minW={{ base: "auto", md: "250px" }}>
            <Text 
              fontSize={{ base: "md", md: "lg" }} 
              fontWeight="bold" 
              color="white" 
              textTransform="uppercase" 
              letterSpacing="wide"
            >
              CONTACT
            </Text>
            <VStack align="start" spacing={2}>
              <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }}>
                {settings?.site_name || "Clover Ridge Technologies LLC"}
              </Text>
              <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }}>
                {settings?.address || "United States"}
              </Text>
              <Link to="/contact">
                <Text 
                  color="gray.300" 
                  fontSize={{ base: "xs", md: "sm" }}
                  wordBreak="break-word"
                  _hover={{ color: "green.500", textDecoration: "underline" }}
                  cursor="pointer"
                >
                  {settings?.email || "hello@cloverridgetech.com"}
                </Text>
              </Link>
              {settings?.phone && (
                <Text 
                  color="gray.300" 
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  {settings.phone}
                </Text>
              )}
            </VStack>
          </VStack>
          
          {/* Navigation Links */}
          <VStack align="start" spacing={3} minW={{ base: "auto", md: "150px" }}>
            <SimpleGrid columns={{ base: 2, md: 1 }} spacing={3} w="full">
              <Link to="/projects">
                <Text 
                  color="white" 
                  fontSize={{ base: "xs", md: "sm" }} 
                  textTransform="uppercase" 
                  letterSpacing="wide" 
                  _hover={{ color: "green.500" }} 
                  transition="color 0.2s"
                >
                  PROJECTS
                </Text>
              </Link>
              <Link to="/about">
                <Text 
                  color="white" 
                  fontSize={{ base: "xs", md: "sm" }} 
                  textTransform="uppercase" 
                  letterSpacing="wide" 
                  _hover={{ color: "green.500" }} 
                  transition="color 0.2s"
                >
                  ABOUT
                </Text>
              </Link>
              <Link to="/engineers">
                <Text 
                  color="white" 
                  fontSize={{ base: "xs", md: "sm" }} 
                  textTransform="uppercase" 
                  letterSpacing="wide" 
                  _hover={{ color: "green.500" }} 
                  transition="color 0.2s"
                >
                  ENGINEERS
                </Text>
              </Link>
              <Link to="/articles">
                <Text
                  color="white"
                  fontSize={{ base: "xs", md: "sm" }}
                  textTransform="uppercase"
                  letterSpacing="wide"
                  _hover={{ color: "green.500" }}
                  transition="color 0.2s"
                >
                  ARTICLES
                </Text>
              </Link>
              <Box
                as="a"
                href="https://learning.cloverridgetech.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text
                  color="white"
                  fontSize={{ base: "xs", md: "sm" }}
                  textTransform="uppercase"
                  letterSpacing="wide"
                  _hover={{ color: "green.500" }}
                  transition="color 0.2s"
                >
                  LEARNING
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
          
          {/* Social Media Links */}
          {(settings?.linkedin_url || settings?.github_url || settings?.twitter_url || settings?.facebook_url) && (
            <VStack align="start" spacing={3} minW={{ base: "auto", md: "120px" }}>
              <Text 
                fontSize={{ base: "md", md: "lg" }} 
                fontWeight="bold" 
                color="white" 
                textTransform="uppercase" 
                letterSpacing="wide"
              >
                CONNECT
              </Text>
              <VStack align="start" spacing={2}>
                {settings?.linkedin_url && (
                  <Box
                    as="a"
                    href={settings.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    _hover={{ color: "green.500" }}
                    transition="color 0.2s"
                  >
                    <HStack spacing={2}>
                      <ExternalLinkIcon boxSize={4} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                        LinkedIn
                      </Text>
                    </HStack>
                  </Box>
                )}
                {settings?.github_url && (
                  <Box
                    as="a"
                    href={settings.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    _hover={{ color: "green.500" }}
                    transition="color 0.2s"
                  >
                    <HStack spacing={2}>
                      <ExternalLinkIcon boxSize={4} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                        GitHub
                      </Text>
                    </HStack>
                  </Box>
                )}
                {settings?.twitter_url && (
                  <Box
                    as="a"
                    href={settings.twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    _hover={{ color: "green.500" }}
                    transition="color 0.2s"
                  >
                    <HStack spacing={2}>
                      <ExternalLinkIcon boxSize={4} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                        Twitter
                      </Text>
                    </HStack>
                  </Box>
                )}
                {settings?.facebook_url && (
                  <Box
                    as="a"
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    _hover={{ color: "green.500" }}
                    transition="color 0.2s"
                  >
                    <HStack spacing={2}>
                      <ExternalLinkIcon boxSize={4} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                        Facebook
                      </Text>
                    </HStack>
                  </Box>
                )}
              </VStack>
            </VStack>
          )}
        </Stack>
        
        {/* Bottom Footer */}
        <Box mt={{ base: 8, md: 16 }} pt={{ base: 6, md: 8 }} borderTop="1px solid" borderColor="gray.800" maxW="5xl" mx="auto">
          <Stack 
            direction={{ base: "column", md: "row" }}
            justify="space-between" 
            align={{ base: "start", md: "center" }} 
            spacing={4}
          >
            <Stack 
              direction={{ base: "column", sm: "row" }}
              spacing={{ base: 2, sm: 4, md: 6 }} 
              flexWrap="wrap"
            >
              <Link to="/projects">
                <Text 
                  color="gray.400" 
                  fontSize="xs" 
                  _hover={{ color: "green.500" }} 
                  transition="color 0.2s"
                >
                  Portfolio
                </Text>
              </Link>
              <Link to="/articles">
                <Text 
                  color="gray.400" 
                  fontSize="xs" 
                  _hover={{ color: "green.500" }} 
                  transition="color 0.2s"
                >
                  Articles
                </Text>
              </Link>
              <Link to="/privacy">
                <Text color="gray.400" fontSize="xs" _hover={{ color: "green.500" }} transition="color 0.2s">Privacy & Cookie Statement</Text>
              </Link>
              <Link to="/contact">
                <Text color="gray.400" fontSize="xs" _hover={{ color: "green.500" }} transition="color 0.2s">Accepting Project Applications</Text>
              </Link>
            </Stack>
            
            <Stack 
              direction={{ base: "column", sm: "row" }}
              spacing={{ base: 2, sm: 4, md: 6 }}
              flexWrap="wrap"
            >
              <Text color="gray.400" fontSize="xs">
                EST. {settings?.founded_year || "2024"}
              </Text>
              <Text color="gray.400" fontSize="xs">
                {settings?.company_status_1 || "REMOTE TEAM"}
              </Text>
              <Text color="gray.400" fontSize="xs">
                {settings?.company_status_2 || "CLOVER RIDGE TECHNOLOGIES LLC"}
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;